import {
  Building2, Clock, Globe, Key, KeyRound, LayoutDashboard, Network, ScrollText, Search,
  ShieldAlert, Tags, Target, Upload, Users, Wrench, BellRing, Plug, CalendarCheck, Tag,
} from 'lucide-react';
import { useApi } from '../contexts/ApiContext';

export interface NavItem {
  name: string;
  href: string;
  icon: typeof Globe;
  /** Extra words the ⌘K palette matches on. */
  keywords?: string;
  adminOnly?: boolean;
  superAdminOnly?: boolean;
  mindMapOnly?: boolean;
  classifyOnly?: boolean;
  whoisOnly?: boolean;
  footprintOnly?: boolean;
  /** Visible to admins and to users holding the can_trigger_sync grant. */
  syncOnly?: boolean;
}

export interface NavGroup {
  /** null = ungrouped (top of the sidebar). */
  label: string | null;
  items: NavItem[];
}

// Single source for the sidebar and the ⌘K palette.
export const NAV_GROUPS: NavGroup[] = [
  { label: null, items: [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard, keywords: 'home overview' },
  ] },
  { label: 'Inventory', items: [
    { name: 'Domains', href: '/domains', icon: Globe, keywords: 'list table' },
    { name: 'Analysis', href: '/domains/manual', icon: Target, adminOnly: true, keywords: 'manual entries' },
    { name: 'Tags', href: '/tags', icon: Tags, classifyOnly: true },
    { name: 'Import', href: '/import', icon: Upload, adminOnly: true, keywords: 'csv upload' },
  ] },
  { label: 'Security', items: [
    { name: 'WHOIS', href: '/whois', icon: Search, whoisOnly: true, keywords: 'exposure reverse lookup' },
    { name: 'Footprint Audit', href: '/footprint-audit', icon: ShieldAlert, footprintOnly: true, keywords: 'clusters alerts' },
    { name: 'Mind Map', href: '/mind-map', icon: Network, mindMapOnly: true, keywords: 'graph emails' },
  ] },
  { label: 'Integrations', items: [
    { name: 'Registrars', href: '/registrars', icon: Building2, syncOnly: true, keywords: 'accounts sync godaddy namecheap porkbun ilkari safenames' },
    { name: 'Account names', href: '/account-names', icon: Tag, adminOnly: true, keywords: 'owner entity purpose naming registrar accounts rename 1password' },
    { name: 'Renewal readiness', href: '/renewal-readiness', icon: CalendarCheck, adminOnly: true, keywords: 'renew auto-renew funds balance payment expiry' },
    { name: '1Password', href: '/1password', icon: KeyRound, adminOnly: true, keywords: 'vault credentials' },
    { name: 'API Setup', href: '/api-setup', icon: Wrench, adminOnly: true },
  ] },
  { label: 'Admin', items: [
    { name: 'Users', href: '/users', icon: Users, adminOnly: true, keywords: 'people permissions' },
    { name: 'API Keys', href: '/api-keys', icon: Key, adminOnly: true, keywords: 'tokens' },
    { name: 'Audit Log', href: '/audit-log', icon: ScrollText, superAdminOnly: true, keywords: 'history changes' },
    { name: 'Crons', href: '/crons', icon: Clock, superAdminOnly: true, keywords: 'scheduled jobs' },
    { name: 'Integrations', href: '/integrations', icon: Plug, superAdminOnly: true, keywords: 'cove tier health status services whoxy 1password' },
    { name: 'Notifications', href: '/notifications/settings', icon: BellRing, superAdminOnly: true, keywords: 'slack alerts digest webhook' },
  ] },
];

/** The nav groups the current user may see (empty groups dropped). */
export function useVisibleNav(): NavGroup[] {
  const { isAdmin, isSuperAdmin, canSeeMindMap, canClassify, canSeeWhois, canSeeFootprintAudit, canTriggerSync } = useApi();
  const visible = (item: NavItem) => {
    if (item.superAdminOnly) return isSuperAdmin;
    if (item.adminOnly) return isAdmin;
    if (item.classifyOnly) return canClassify;
    if (item.mindMapOnly) return canSeeMindMap;
    if (item.whoisOnly) return canSeeWhois;
    if (item.footprintOnly) return canSeeFootprintAudit;
    if (item.syncOnly) return canTriggerSync;
    return true;
  };
  return NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter(visible) }))
    .filter((g) => g.items.length > 0);
}

/** The nav item a path belongs to: the longest matching href (so /domains/abc → Domains, /domains/manual → Analysis). */
export function activeHref(pathname: string, groups: NavGroup[]): string | null {
  let best: string | null = null;
  for (const g of groups) for (const i of g.items) {
    const match = i.href === '/' ? pathname === '/' : pathname === i.href || pathname.startsWith(i.href + '/');
    if (match && (!best || i.href.length > best.length)) best = i.href;
  }
  return best;
}
